import { useState } from 'react';
import { useLayoutContext } from 'context/LayoutContext';
import theme from 'theme';

const MIN_COLUMN_WIDTH = 80;
const COLUMN_RESIZE_HANDLE_SIZE = 20;
const COLUMN_RESIZE_LINE_WIDTH = 3;
const LEGACY_COLUMN_RESIZE_HANDLE_SIZE = 6;
const LEGACY_COLUMN_RESIZE_LINE_WIDTH = 2;

interface ColumnResizeHandleProps {
  columnType: string;
  colLegend: string;
  defaultWidth?: number;
  edge?: 'left' | 'right';
  onResize: (columnType: string, width: number) => void;
  variant?: 'default' | 'legend';
}

const getColumnCells = (colLegend: string, columnType: string, table: HTMLElement): HTMLElement[] => {
  // Empty legend matches every cell with blank data-column-legend — never use it.
  if (colLegend) {
    return Array.from(table.querySelectorAll<HTMLElement>(`[data-column-legend="${colLegend}"]`));
  }
  if (columnType) {
    return Array.from(table.querySelectorAll<HTMLElement>(`[data-column-ref="${columnType}"]`));
  }
  return [];
};

const updateColumnCells = (colLegend: string, columnType: string, table: HTMLElement, newWidth: number) => {
  const allCells = getColumnCells(colLegend, columnType, table);
  allCells.forEach(el => {
    // eslint-disable-next-line no-param-reassign
    el.style.cssText += `width:${newWidth}px;min-width:${newWidth}px;max-width:${newWidth}px;`;
  });
};

const ColumnResizeHandle = ({
  columnType,
  colLegend,
  defaultWidth,
  edge = 'right',
  onResize,
  variant = 'default',
}: ColumnResizeHandleProps) => {
  const { isExportMode } = useLayoutContext();
  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const isLegendVariant = variant === 'legend';

  if (isExportMode) {
    return null;
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const sourceCell = (event.currentTarget.closest('td') || event.currentTarget.closest('th')) as HTMLElement | null;
    const table = sourceCell?.closest('table') as HTMLElement | null;
    if (!sourceCell || !table) {
      return;
    }
    const targetCell = isLegendVariant
      ? getColumnCells(colLegend, columnType, table)[0] ?? sourceCell
      : sourceCell;

    const ownerWindow = event.currentTarget.ownerDocument.defaultView ?? window;
    const startX = event.clientX;
    const startWidth = targetCell.offsetWidth;
    let currentWidth = startWidth;
    let hasMoved = false;
    setIsResizing(true);

    const handleMouseMove = (e: MouseEvent) => {
      hasMoved = true;
      const delta = e.clientX - startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + delta);
      currentWidth = newWidth;

      targetCell.style.width = `${newWidth}px`;
      targetCell.style.minWidth = `${newWidth}px`;
      targetCell.style.maxWidth = `${newWidth}px`;
      updateColumnCells(colLegend, columnType, table, newWidth);
    };

    const handleMouseUp = () => {
      ownerWindow.removeEventListener('mousemove', handleMouseMove);
      ownerWindow.removeEventListener('mouseup', handleMouseUp);
      setIsResizing(false);

      if (hasMoved && currentWidth !== startWidth) {
        onResize(columnType, currentWidth);
      }
    };

    ownerWindow.addEventListener('mousemove', handleMouseMove);
    ownerWindow.addEventListener('mouseup', handleMouseUp);
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (defaultWidth == null) {
      return;
    }

    const sourceCell = (event.currentTarget.closest('td') || event.currentTarget.closest('th')) as HTMLElement | null;
    const table = sourceCell?.closest('table') as HTMLElement | null;
    if (!sourceCell || !table) {
      return;
    }
    const targetCell = isLegendVariant
      ? getColumnCells(colLegend, columnType, table)[0] ?? sourceCell
      : sourceCell;

    targetCell.style.width = `${defaultWidth}px`;
    targetCell.style.minWidth = `${defaultWidth}px`;
    targetCell.style.maxWidth = `${defaultWidth}px`;
    updateColumnCells(colLegend, columnType, table, defaultWidth);
    onResize(columnType, defaultWidth);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        alignItems: 'center',
        cursor: 'col-resize',
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        overflow: 'visible',
        position: 'absolute',
        ...(edge === 'left' ? { left: 0 } : { right: 0 }),
        top: 0,
        transform: isLegendVariant ? `translateX(${edge === 'left' ? '-50%' : '50%'})` : undefined,
        touchAction: 'none',
        width: `${isLegendVariant ? COLUMN_RESIZE_HANDLE_SIZE : LEGACY_COLUMN_RESIZE_HANDLE_SIZE}px`,
        zIndex: isLegendVariant ? 3 : 1,
      }}>
      <div
        style={{
          backgroundColor: isHovered || isResizing ? theme.palette.primary.main : 'transparent',
          borderRadius: '1px',
          height: isLegendVariant ? '100%' : '70%',
          left: '50%',
          position: 'absolute',
          ...(isLegendVariant
            ? {
              top: 0,
              transform: 'translateX(-50%)',
            }
            : {
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }),
          transition: 'background-color 0.15s ease',
          width: `${isLegendVariant ? COLUMN_RESIZE_LINE_WIDTH : LEGACY_COLUMN_RESIZE_LINE_WIDTH}px`,
        }}
      />
    </div>
  );
};

export default ColumnResizeHandle;
